import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, Clock, Inbox, Send, Archive, Repeat, ThumbsUp } from "lucide-react";
import { api, imgSrc } from "@/lib/api";
import { trackOfferAction } from "@/lib/analytics";
import { useAuth } from "@/contexts/AuthContext";
import RateUserModal from "@/components/RateUserModal";
import CounterOfferModal from "@/components/CounterOfferModal";
import { toast } from "sonner";

const TABS = [
  { id: "incoming", label: "Incoming", icon: Inbox },
  { id: "outgoing", label: "Sent", icon: Send },
  { id: "active", label: "Active", icon: Clock },
  { id: "history", label: "History", icon: Archive },
];

export default function MyTrades() {
  const { user } = useAuth();
  const [tab, setTab] = useState("incoming");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rateContext, setRateContext] = useState(null); // { offer, otherUserId, otherUserName }
  const [counterOffer, setCounterOffer] = useState(null);

  const load = async (which) => {
    setLoading(true);
    try {
      const { data } = await api.get("/trade-offers", { params: { box: which } });
      setItems(data.items || []);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(tab); }, [tab]);

  const promptVouch = (other) => {
    if (!other?.user_id) return;
    toast.success("Trade complete! 🎉", {
      description: `Enjoyed trading with ${other.name}? Give them a public vouch.`,
      duration: 9000,
      action: {
        label: "Vouch 🤝",
        onClick: async () => {
          try {
            await api.post(`/users/${other.user_id}/vouch`);
            toast.success(`You vouched for ${other.name} 🤝`);
          } catch (e) {
            toast.error(e.response?.data?.detail || "Could not vouch");
          }
        },
      },
    });
  };

  const action = async (id, what, other) => {
    try {
      const { data } = await api.post(`/trade-offers/${id}/action`, { action: what });
      trackOfferAction(what, data);
      if (what === "complete") {
        if (data?.status === "completed") {
          promptVouch(other);
        } else {
          toast.success("Marked complete — waiting for the other party to confirm");
        }
      } else {
        toast.success(`Offer ${what}ed`);
      }
      load(tab);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };

  return (
    <div className="section-pad py-8 md:py-12" data-testid="my-trades-page">
      <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight">My trades</h1>
      <p className="text-zinc-500 mt-2">Track every offer in one place</p>

      {/* Tabs */}
      <div className="mt-6 flex items-center gap-2 overflow-x-auto no-scrollbar">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} className={`btn-pill !text-sm ${tab === t.id ? "btn-primary" : "btn-soft"}`} data-testid={`tab-${t.id}`}>
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      <div className="mt-8 space-y-4">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => <div key={i} className="vyrti-card p-6"><div className="skeleton h-24" /></div>)
        ) : items.length === 0 ? (
          <div className="vyrti-card p-12 text-center" data-testid="trades-empty">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#F1F5F9] flex items-center justify-center mb-3">
              <Repeat className="w-7 h-7 text-zinc-400" />
            </div>
            <p className="text-zinc-600">No trades here yet.</p>
            <Link to="/browse" className="btn-pill btn-accent mt-4 inline-flex" data-testid="trades-browse">Browse trades</Link>
          </div>
        ) : items.map(o => {
          const incoming = o.to_user_id === user.user_id;
          const other = incoming ? o.from_user : o.to_user;
          return (
            <div key={o.id} className="vyrti-card p-5" data-testid={`offer-${o.id}`}>
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  {other?.picture ? (
                    <img src={imgSrc(other.picture)} alt="" className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-[#F1F5F9] flex items-center justify-center font-semibold">{other?.name?.[0]?.toUpperCase()}</div>
                  )}
                  <div>
                    <p className="text-sm font-medium">{incoming ? "From" : "To"} {other?.name}</p>
                    <p className="text-xs text-zinc-500">{new Date(o.created_at).toLocaleString()}</p>
                  </div>
                </div>
                <span className={`vyrti-pill text-[11px] !py-1 ${
                  o.status === "pending" ? "!bg-amber-100 !text-amber-700" :
                  o.status === "accepted" ? "!bg-emerald-100 !text-emerald-700" :
                  o.status === "rejected" || o.status === "cancelled" ? "!bg-rose-100 !text-rose-700" :
                  o.status === "expired" ? "!bg-zinc-200 !text-zinc-600" :
                  o.status === "completed" ? "!bg-[#E0EDFA] !text-[#0654BA]" :
                  "!bg-zinc-100"
                }`}>{o.status}</span>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-4">
                <div>
                  <p className="text-[11px] text-zinc-500 mb-1.5">{incoming ? "They offer" : "You offer"}</p>
                  <div className="space-y-1.5">
                    {(o.offered_listings || []).map(l => (
                      <Link key={l.id} to={`/listing/${l.id}`} className="flex items-center gap-2 bg-[#F7F7F7] rounded-xl p-2 hover:bg-[#F1F5F9]">
                        {l.images?.[0] && <img src={imgSrc(l.images[0])} alt="" className="w-10 h-10 rounded-lg object-cover" />}
                        <p className="text-sm font-medium truncate">{l.title}</p>
                      </Link>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-[11px] text-zinc-500 mb-1.5">{incoming ? "For your" : "For their"}</p>
                  <div className="space-y-1.5">
                    {(o.target_listings || (o.target_listing ? [o.target_listing] : [])).map(l => (
                      <Link key={l.id} to={`/listing/${l.id}`} className="flex items-center gap-2 bg-[#F7F7F7] rounded-xl p-2 hover:bg-[#F1F5F9]">
                        {l.images?.[0] && <img src={imgSrc(l.images[0])} alt="" className="w-10 h-10 rounded-lg object-cover" />}
                        <p className="text-sm font-medium truncate">{l.title}</p>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              {o.message && (
                <p className="mt-3 text-sm text-zinc-600 bg-[#F7F7F7] rounded-xl p-3 italic">"{o.message}"</p>
              )}

              {/* Actions */}
              {o.status === "pending" && (
                <div className="mt-4 flex gap-2 flex-wrap">
                  <Link to={`/messages/${o.conversation_id}`} className="btn-pill btn-soft !text-sm !py-2" data-testid={`open-chat-${o.id}`}>
                    Open chat
                  </Link>
                  {incoming ? (
                    <>
                      <button onClick={() => action(o.id, "accept")} className="btn-pill btn-accent !text-sm !py-2" data-testid={`accept-${o.id}`}>
                        <Check className="w-4 h-4" /> Accept
                      </button>
                      <button onClick={() => setCounterOffer(o)} className="btn-pill !text-sm !py-2 !bg-[#E0EDFA] !text-[#0654BA] hover:!bg-[#dbeafe]" data-testid={`counter-${o.id}`}>
                        <Repeat className="w-4 h-4" /> Counter
                      </button>
                      <button onClick={() => action(o.id, "reject")} className="btn-pill btn-soft !text-sm !py-2" data-testid={`decline-${o.id}`}>
                        <X className="w-4 h-4" /> Decline
                      </button>
                    </>
                  ) : (
                    <button onClick={() => action(o.id, "cancel")} className="btn-pill btn-soft !text-sm !py-2" data-testid={`cancel-${o.id}`}>
                      Cancel
                    </button>
                  )}
                </div>
              )}
              {o.status === "accepted" && (
                <div className="mt-4 flex gap-2 flex-wrap">
                  <Link to={`/messages/${o.conversation_id}`} className="btn-pill btn-accent !text-sm !py-2" data-testid={`coord-${o.id}`}>
                    Coordinate meetup
                  </Link>
                  {(o.completed_by || []).includes(user?.user_id) ? (
                    <span className="btn-pill btn-soft !text-sm !py-2 opacity-70 cursor-default" data-testid={`waiting-confirm-${o.id}`}>
                      Waiting for {other?.name || "them"} to confirm
                    </span>
                  ) : (
                    <button onClick={() => action(o.id, "complete", other)} className="btn-pill btn-soft !text-sm !py-2" data-testid={`complete-${o.id}`}>
                      {(o.completed_by || []).length > 0 ? "Confirm completion" : "Mark complete"}
                    </button>
                  )}
                  <button
                    onClick={() => setRateContext({ offer: o, otherUserId: incoming ? o.from_user_id : o.to_user_id, otherUserName: other?.name || "" })}
                    className="btn-pill !bg-emerald-100 !text-emerald-700 hover:!bg-emerald-200 !text-sm !py-2"
                    data-testid={`rate-${o.id}`}
                  >
                    <ThumbsUp className="w-4 h-4" /> Rate trade
                  </button>
                </div>
              )}
              {o.status === "completed" && (
                <div className="mt-4 flex gap-2 flex-wrap">
                  <button
                    onClick={() => setRateContext({ offer: o, otherUserId: incoming ? o.from_user_id : o.to_user_id, otherUserName: other?.name || "" })}
                    className="btn-pill !bg-emerald-100 !text-emerald-700 hover:!bg-emerald-200 !text-sm !py-2"
                    data-testid={`rate-${o.id}`}
                  >
                    <ThumbsUp className="w-4 h-4" /> Rate trade
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <RateUserModal
        open={!!rateContext}
        onClose={() => setRateContext(null)}
        offer={rateContext?.offer || {}}
        otherUserId={rateContext?.otherUserId}
        otherUserName={rateContext?.otherUserName}
        onRated={() => load(tab)}
      />
      <CounterOfferModal
        open={!!counterOffer}
        onClose={() => setCounterOffer(null)}
        offer={counterOffer}
        onCountered={() => load(tab)}
      />
    </div>
  );
}
